import { useCallback, useState } from "react";
import { LocalStorage } from "@/utils/LocalStorage";
import { LocalStorageKeys } from "@/config/localStorage";
import { WIDGET_REGISTRY } from "./widgetRegistry";

interface WidgetPref {
	id: string;
	enabled: boolean;
}

export interface DashboardPreferences {
	widgets: WidgetPref[];
}

const getDefaults = (): DashboardPreferences => ({
	widgets: WIDGET_REGISTRY.map(w => ({ id: w.id, enabled: w.defaultEnabled })),
});

const mergeWithDefaults = (stored: Partial<DashboardPreferences> | null): DashboardPreferences => {
	const defaults = getDefaults();
	if (!stored) return defaults;

	const storedMap = new Map((stored.widgets ?? []).map(w => [w.id, w]));
	const widgets = defaults.widgets.map(def => {
		const s = storedMap.get(def.id);
		return s ? { ...def, enabled: s.enabled } : def;
	});

	return { widgets };
};

export const useDashboardPreferences = () => {
	const [prefs, setPrefsState] = useState<DashboardPreferences>(() => {
		const stored = LocalStorage.get<LocalStorageKeys.DASHBOARD_PREFERENCES, Partial<DashboardPreferences>>(
			LocalStorageKeys.DASHBOARD_PREFERENCES,
		);
		return mergeWithDefaults(stored);
	});

	const setWidgetEnabled = useCallback((id: string, enabled: boolean) => {
		setPrefsState(prev => {
			const next = {
				...prev,
				widgets: prev.widgets.map(w => (w.id === id ? { ...w, enabled } : w)),
			};
			LocalStorage.set(LocalStorageKeys.DASHBOARD_PREFERENCES, next);
			return next;
		});
	}, []);

	const isWidgetEnabled = useCallback(
		(id: string) => {
			const reg = WIDGET_REGISTRY.find(w => w.id === id);
			if (reg?.alwaysVisible) return true;
			return prefs.widgets.find(w => w.id === id)?.enabled ?? true;
		},
		[prefs],
	);

	return { prefs, setWidgetEnabled, isWidgetEnabled };
};
